/**
 * IsConversationBlockedUseCase
 *
 * Returns whether the current user has blocked the conversation with the
 * given peer. A conversation is considered blocked iff a record is present
 * in encrypted local storage under the
 * `SECURE_STORAGE_KEYS.BLOCKED_CONVERSATIONS + peerId` key.
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:389-396 —
 * `getConversationIsBlocked()` opens the encrypted `BlockedConversation`
 * Hive box and returns `box.get(BlockedConversation.keyName + peerUserId)
 * != null`. The web equivalent lives in `SecureStorageWebCrypto` keyed by
 * the same prefix + peerId.
 *
 * Architectural note: this is the ONLY use case in the chat folder that
 * touches `SecureStorageWebCrypto` directly — there is no chat-side service
 * hop for this lookup because the Flutter side reads Hive in the screen
 * itself (no data source method exists to mirror). Other chat use cases
 * must continue to route SecureStorage access through a service.
 */

import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';

export class IsConversationBlockedUseCase {
  private readonly storage = SecureStorageWebCrypto.getInstance();

  /**
   * Check whether the current user has blocked the conversation with `peerId`.
   *
   * @param peerId - The peer user's firebase id (Flutter: `_peerUser!.firebaseId`).
   * @returns `true` iff a `BlockedConversation` record exists in encrypted
   *   storage under the conversation key; `false` otherwise.
   */
  async execute(peerId: string): Promise<boolean> {
    const storageKey = SECURE_STORAGE_KEYS.BLOCKED_CONVERSATIONS + peerId;
    const record = await this.storage.getEncrypted<unknown>(storageKey);
    return record !== null;
  }
}
